import { apiClient } from '@/api/apiClient'
import type { Quarter } from '@/config/cbamReportingPeriods'
import type { CbamModifiers } from '@/shared/types/cbamModifiers'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export type CbamReport = {
  id: string
  periodId: string
  isCompleted: boolean
  version: number
  createdAt: string
  updatedAt: string
  organizationId: string
  modifiers?: CbamModifiers
}

type CompleteCbamReportPayload = {
  year: number
  quarter: number
}

export const cbamReportsQueryKey = 'cbamReports'

export function cbamReportingBasicInfoCompleted({
  city,
  identificationNumber
}: {
  city?: string
  identificationNumber?: string
}): boolean {
  return !!city && !!identificationNumber
}

export const useDownloadStandaloneCbamReport = (
  organizationId: string,
  cbamReportId: string,
  format: 'zip' | 'pdf'
) => {
  return useMutation<Blob, Error>({
    mutationFn: async () => {
      const response = await apiClient.get<Blob>(
        `/v2/organizations/${organizationId}/cbam-reports/${cbamReportId}/download`,
        {
          timeout: 300000, // 5 minutes
          params: {
            format
          },
          responseType: 'blob'
        }
      )

      return response.data
    }
  })
}

export type CbamReportingDataExportFormat = 'csv' | 'xlsx'

export type CbamReportingDataExportPayload = {
  quarters: Quarter[]
  format: CbamReportingDataExportFormat
}

export const useExportCbamReportingData = (organizationId: string, cbamReportId: string) => {
  return useMutation<Blob, Error, CbamReportingDataExportPayload>({
    mutationFn: async payload => {
      const response = await apiClient.post<Blob>(
        `/v2/organizations/${organizationId}/cbam-reports/${cbamReportId}/reporting-data-export`,
        payload,
        {
          timeout: 300000,
          responseType: 'blob'
        }
      )
      return response.data
    }
  })
}

export const useGetAllCbamReports = (organizationId: string) => {
  return useQuery({
    queryKey: [cbamReportsQueryKey, organizationId],
    queryFn: async () => {
      const response = await apiClient.get<CbamReport[]>(
        `/v2/organizations/${organizationId}/cbam-reports`
      )
      return response.data
    }
  })
}

export const useGetAllDeclarantCbamReports = (organizationId: string, declarantId: string) => {
  return useQuery({
    queryKey: [cbamReportsQueryKey, organizationId, declarantId],
    queryFn: async () => {
      const response = await apiClient.get<CbamReport[]>(
        `/v2/organizations/${organizationId}/declarants/${declarantId}/cbam-reports`
      )
      return response.data
    }
  })
}

export const useCompleteCbamReport = (organizationId: string, cbamReportId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CompleteCbamReportPayload) => {
      const response = await apiClient.post<CbamReport>(
        `/v2/organizations/${organizationId}/cbam-reports/${cbamReportId}/complete`,
        payload,
        {
          timeout: 300000 // 5 minutes
        }
      )

      queryClient.setQueryData<CbamReport[]>(
        [cbamReportsQueryKey, organizationId],
        oldData =>
          oldData?.map(cbamReport =>
            cbamReport.id === cbamReportId ? response.data : cbamReport
          ) || []
      )
      return response.data
    }
  })
}

export const useReopenCbamReport = (organizationId: string, cbamReportId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<CbamReport>(
        `/v2/organizations/${organizationId}/cbam-reports/${cbamReportId}/reopen`
      )

      queryClient.setQueryData<CbamReport[]>(
        [cbamReportsQueryKey, organizationId],
        oldData =>
          oldData?.map(cbamReport =>
            cbamReport.id === cbamReportId ? response.data : cbamReport
          ) || []
      )
      return response.data
    }
  })
}

type UpdateCbamReportModifiersPayload = {
  modifiers: CbamModifiers
}

export const useUpdateCbamReportModifiers = (organizationId: string, cbamReportId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: UpdateCbamReportModifiersPayload) => {
      const response = await apiClient.patch<CbamReport>(
        `/v2/organizations/${organizationId}/cbam-reports/${cbamReportId}/modifiers`,
        payload
      )
      return response.data
    },
    onSuccess: data => {
      queryClient.setQueryData<CbamReport[]>(
        [cbamReportsQueryKey, organizationId],
        old =>
          old?.map(report => (report.id === data.id ? { ...report, ...data } : report)) ?? []
      )
      queryClient.invalidateQueries({ queryKey: [cbamReportsQueryKey, organizationId] })
    }
  })
}
